export enum RegionalHighlightSlotState {
  AVAILABLE = 'AVAILABLE',
  OCCUPIED = 'OCCUPIED',
  NOT_STARTED = 'NOT_STARTED',
  EXPIRED = 'EXPIRED',
}

export enum RegionalHighlightSlotDisabledReason {
  CONTRACT_NOT_STARTED = 'CONTRACT_NOT_STARTED',
  CONTRACT_EXPIRED = 'CONTRACT_EXPIRED',
  DISTRICT_UNRESOLVED = 'DISTRICT_UNRESOLVED',
  SIBLING_ASSIGNMENT = 'SIBLING_ASSIGNMENT',
  UNRELATED_ASSIGNMENT = 'UNRELATED_ASSIGNMENT',
}

export enum RegionalHighlightCustomerRelation {
  DIRECT_CUSTOMER = 'DIRECT_CUSTOMER',
  HEAD_SUB_CUSTOMER = 'HEAD_SUB_CUSTOMER',
  SAME_HEAD_SIBLING = 'SAME_HEAD_SIBLING',
  UNRELATED_CUSTOMER = 'UNRELATED_CUSTOMER',
}

export enum RegionalHighlightListingMatchReason {
  ELIGIBLE = 'ELIGIBLE',
  LISTING_INELIGIBLE = 'LISTING_INELIGIBLE',
  DISTRICT_MISMATCH = 'DISTRICT_MISMATCH',
  SLOT_OCCUPIED = 'SLOT_OCCUPIED',
  SLOT_EXPIRED = 'SLOT_EXPIRED',
  SLOT_NOT_STARTED = 'SLOT_NOT_STARTED',
  SLOT_DISTRICT_UNRESOLVED = 'SLOT_DISTRICT_UNRESOLVED',
}

export enum RegionalHighlightListingMatchAction {
  ASSIGN = 'ASSIGN',
  REPLACE = 'REPLACE',
}

export enum RegionalHighlightAddressMatchReason {
  ELIGIBLE = 'ELIGIBLE',
  ADDRESS_INELIGIBLE = 'ADDRESS_INELIGIBLE',
  SLOT_DISTRICT_UNRESOLVED = 'SLOT_DISTRICT_UNRESOLVED',
  DISTRICT_MISMATCH = 'DISTRICT_MISMATCH',
  SLOT_EXPIRED = 'SLOT_EXPIRED',
  SLOT_NOT_STARTED = 'SLOT_NOT_STARTED',
  SLOT_OCCUPIED = 'SLOT_OCCUPIED',
}

export interface RegionalHighlightAddressMatch {
  assignable: boolean;
  reason: RegionalHighlightAddressMatchReason;
  action?: RegionalHighlightListingMatchAction;
}

export interface RegionalHighlightDraftAddress {
  postalCode: string;
  locality?: string;
  street?: string;
  latitude?: number;
  longitude?: number;
}

export interface RegionalHighlightResolvedAddress {
  postalCode: string;
  districtId?: string;
  districtName?: Record<string, string>;
  eligible: boolean;
  ineligibleReason?: 'ADDRESS_DISTRICT_UNRESOLVED';
}

export interface RegionalHighlightSlotControls {
  canAssign: boolean;
  canReplace: boolean;
  canDeactivate: boolean;
  canExtend?: boolean;
  canRenew?: boolean;
  canToggleAutoRenew?: boolean;
  disabledReason?: RegionalHighlightSlotDisabledReason;
}

export interface RegionalHighlightActiveBooking {
  bookingId: string;
  bookingCustomerId: string;
  listingId: string;
  listingListerId?: string;
  begin: string;
  staleBecause?:
    | RegionalHighlightSlotDisabledReason.CONTRACT_NOT_STARTED
    | RegionalHighlightSlotDisabledReason.CONTRACT_EXPIRED;
  customerRelation?: RegionalHighlightCustomerRelation;
}

export interface RegionalHighlightListingMatch {
  assignable: boolean;
  reason: RegionalHighlightListingMatchReason;
  action?: RegionalHighlightListingMatchAction;
}

export enum RegionalHighlightAssignableListingsSource {
  ACTIVE = 'ACTIVE',
  REFERENCE = 'REFERENCE',
}

export type RegionalHighlightAssignableListingsMode = 'assign' | 'replace';

export interface RegionalHighlightAssignableListingsRequest {
  source: RegionalHighlightAssignableListingsSource;
  mode: RegionalHighlightAssignableListingsMode;
  page?: number;
  pageSize?: number;
  q?: string;
}

export interface RegionalHighlightAssignableListing {
  listingId: string;
  listingMatch?: RegionalHighlightListingMatch;
  listing?: Record<string, unknown>;
  resolvedListing?: RegionalHighlightResolvedListing;
}

export interface RegionalHighlightAssignableListingsResponse {
  slotId: string;
  districtId?: string;
  districtLabel?: string;
  source: RegionalHighlightAssignableListingsSource;
  mode: RegionalHighlightAssignableListingsMode;
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  listings: RegionalHighlightAssignableListing[];
}

export interface RegionalHighlightSlot {
  slotId: string;
  slotOwnerCustomerId: string;
  productId: string;
  productCode: string;
  productName: string;
  districtId?: string | null;
  districtLabel?: string | null;
  districtResolutionStatus: 'RESOLVED' | 'MISSING_LABEL' | 'UNKNOWN_LABEL';
  contractStartDate: string;
  contractEndDate?: string | null;
  status: RegionalHighlightSlotState;
  controls: RegionalHighlightSlotControls;
  activeBooking?: RegionalHighlightActiveBooking;
  listingMatch?: RegionalHighlightListingMatch;
  addressMatch?: RegionalHighlightAddressMatch;
  position?: number;
  monthlyPriceChfCents?: number;
  bookedTermMonths?: number;
  autoRenew?: boolean;
}

export interface RegionalHighlightResolvedListing {
  listingId: string;
  resolved: boolean;
  offerType?: 'BUY' | 'RENT';
  districtId?: string;
  districtName?: Record<string, string>;
  eligible: boolean;
  ineligibleReason?: 'LISTING_NOT_FOUND' | 'LISTING_NOT_FOR_SALE' | 'LISTING_DISTRICT_UNRESOLVED';
}

export interface RegionalHighlightSlotDistrictAggregates {
  positionsHeld: number;
  positionsTotal: number;
  searchesPerMonth: number;
  monthlyPriceChfCents?: number;
  monthlyPriceChf?: number;
}

export interface RegionalHighlightSlotsResponse {
  slots: RegionalHighlightSlot[];
  listing?: RegionalHighlightResolvedListing;
  address?: RegionalHighlightResolvedAddress;
  districtAggregates?: Record<string, RegionalHighlightSlotDistrictAggregates>;
}

export interface RegionalHighlightListingStatusRequest {
  listingIds: string[];
}

export interface RegionalHighlightListingStatus {
  hasRegionalHighlight: boolean;
  bookingId?: string;
  slotId?: string;
  district?: string;
  districtLabel?: string;
  contractStartDate?: string;
  contractEndDate?: string | null;
}

export type RegionalHighlightListingStatusesByListingId = Record<
  string,
  RegionalHighlightListingStatus
>;

export interface RegionalHighlightAssignmentRequest {
  listingId: string;
  replaceBookingId?: string;
}

export interface RegionalHighlightAssignmentResponse {
  bookingId: string;
  slotId: string;
}

export interface RegionalHighlightDeactivationRequest {
  bookingId?: string;
  listingId?: string;
}

export enum RegionalHighlightCallbackEntryPoint {
  LISTING_CREATION_STEP_3 = 'LISTING_CREATION_STEP_3',
  VISIBILITY_UPGRADE_MODAL = 'VISIBILITY_UPGRADE_MODAL',
}

export interface RegionalHighlightCallbackRequest {
  districtId: string;
  districtLabel?: string;
  listingId?: string;
  entryPoint: RegionalHighlightCallbackEntryPoint;
  locale?: string;
}

export interface RegionalHighlightCallbackResponse {
  requestId: string;
  status: string;
}

export type RegionalHighlightPurchaseDurationMonths = 1 | 3 | 6 | 12;

export interface RegionalHighlightPurchaseOption {
  districtId: string;
  districtName?: Record<string, string>;
  districtLabel: string;
  positionsTotal: number;
  positionsSold: number;
  positionsFree: number;
  monthlyPriceByDuration: Partial<Record<RegionalHighlightPurchaseDurationMonths, number>>;
  productCodeByDuration: Partial<Record<RegionalHighlightPurchaseDurationMonths, string>>;
  searchesPerMonth: number;
  purchasable: boolean;
  purchasableReason?: string;
  alreadyHoldsPosition?: boolean;
}

export interface RegionalHighlightPurchaseOptionsResponse {
  options: RegionalHighlightPurchaseOption[];
}

export enum RegionalHighlightPurchaseOrderStatus {
  REQUESTED = 'REQUESTED',
  FULFILLED = 'FULFILLED',
  FAILED = 'FAILED',
  TIMED_OUT = 'TIMED_OUT',
}

export interface RegionalHighlightPurchaseOrderRequest {
  idempotencyKey: string;
  districtId: string;
  durationMonths: RegionalHighlightPurchaseDurationMonths;
  contractStartDate: string;
  listingId?: string;
  locale: string;
  renewSlotId?: string;
}

export interface RegionalHighlightPurchaseOrder {
  orderId: string;
  status: RegionalHighlightPurchaseOrderStatus;
  districtId: string;
  districtLabel?: string;
  durationMonths: RegionalHighlightPurchaseDurationMonths;
  productCode?: string;
  monthlyPriceChfCents?: number;
  termTotalChfCents?: number;
  listingId?: string;
  locale?: string;
  requestedAt: string;
  contractStartDate?: string;
  renewSlotId?: string;
}

export interface RegionalHighlightPurchaseOrdersResponse {
  orders: RegionalHighlightPurchaseOrder[];
}
